/**
 * Reviewed probate filing catalogues.
 *
 * Every widget of every supported court PDF is declared exactly once, as one of:
 *  - a shared record source (estate, decedent, participants, assets, totals...),
 *  - a document-specific filing control the user answers in "Filing details",
 *  - an execution slot (signature, notary, court or clerk use) that software must
 *    leave blank, or a slot that does not apply, with the reason.
 *
 * PDF text extraction is never an input schema: labels here were written by
 * reading the printed form. This file has no imports so the frontend can copy it.
 */
export type ControlType = 'text' | 'textarea' | 'number' | 'date' | 'select' | 'participant' | 'participants';
export type Execution = 'signature' | 'notary' | 'court' | 'notApplicable';

export interface FilingControl {
  key: string;
  label: string;
  section: string;
  type: ControlType;
  options?: { value: string; label: string }[];
  help?: string;
  /** participants: how many rows the printed form holds. Extra rows are rejected. */
  max?: number;
  /** participants: optional free-text column asked for each row (for example a relationship). */
  note?: string;
  /** Characters the printed box holds (from the PDF), added when the schema is served. */
  maxLength?: number;
}

export interface FilingMapping {
  pdfField: string;
  /** "entity.path"; null only for execution slots. */
  source: string | null;
  label: string;
  template?: string;
  selector?: string;
  execution?: Execution;
  /** Where the value is entered or how it is computed, for shared sources. */
  location?: string;
}

export interface ProbateFilingSchema {
  file: string;
  title: string;
  controls: FilingControl[];
  mappings: FilingMapping[];
}

/** Fields exposed for each selected person, as `probateFiling.<key>_<field>`. */
export const PERSON_FIELDS = [
  'fullName', 'firstName', 'middleName', 'lastName', 'ssn', 'phone', 'fax', 'email',
  'street', 'addressLine2', 'city', 'state', 'zipCode', 'county', 'cityStateZip', 'fullAddress',
  'dateOfDeath', 'dob', 'relationshipToDecedent', 'citizenship', 'firmName', 'otherNames',
  /** "Name; address; citizenship" (plus the row note for lists), for single-line table rows. */
  'line',
] as const;
export type PersonField = typeof PERSON_FIELDS[number];

export const SIGNATURE_NOTE = 'Signed by hand (or e-signature workflow). Not completed by this software.';
export const NOTARY_NOTE = 'Completed by the notary or commissioner of deeds at signing.';
export const COURT_NOTE = 'Completed by the court or its clerk.';

export class FilingBuilder {
  readonly schema: ProbateFilingSchema;
  constructor(file: string, title: string) { this.schema = { file, title, controls: [], mappings: [] }; }

  private add(m: FilingMapping) {
    if (this.schema.mappings.some(x => x.pdfField === m.pdfField)) throw new Error(`${this.schema.title}: ${m.pdfField} declared twice`);
    this.schema.mappings.push(m);
    return this;
  }
  private control(c: FilingControl) {
    const existing = this.schema.controls.find(x => x.key === c.key);
    if (existing) {
      if (existing.type !== c.type) throw new Error(`${this.schema.title}: control ${c.key} redeclared with another type`);
      return this;
    }
    this.schema.controls.push(c);
    return this;
  }

  /** A shared record value, e.g. `estate.county`, `decedent.fullName`, `totals.x`. */
  shared(pdfField: string, source: string, label: string, extra: Partial<FilingMapping> = {}) {
    return this.add({ pdfField, source, label, ...extra });
  }
  /** Several shared values composed into one printed line. */
  template(pdfField: string, template: string, label: string, extra: Partial<FilingMapping> = {}) {
    const first = /\{\{\s*([a-zA-Z]+)(?:\[[^\]]*\])*\.?([^}\s]*)\s*\}\}/.exec(template);
    const source = first ? `${first[1]}.${first[2] || 'fullName'}` : null;
    return this.add({ pdfField, source, label, template, ...extra });
  }
  /** A document-specific answer, stored once per estate and document. */
  input(pdfField: string, key: string, label: string, section: string, type: 'text' | 'textarea' | 'number' | 'date' = 'text', help?: string) {
    this.control({ key, label, section, type, ...(help ? { help } : {}) });
    return this.add({ pdfField, source: `probateFiling.${key}`, label });
  }
  /** Declare a document-specific question without printing it directly (used by templates). */
  question(key: string, label: string, section: string, type: 'text' | 'textarea' | 'number' | 'date' = 'text', help?: string) {
    return this.control({ key, label, section, type, ...(help ? { help } : {}) });
  }
  /** Print an existing control's value (for a second copy of the same answer). */
  answer(pdfField: string, key: string, label: string) {
    if (!this.schema.controls.some(c => c.key === key)) throw new Error(`${this.schema.title}: unknown control ${key}`);
    return this.add({ pdfField, source: `probateFiling.${key}`, label });
  }
  /** A dropdown/combo PDF field whose printed values must match the PDF options exactly. */
  dropdown(pdfField: string, key: string, label: string, section: string, values: string[], help?: string) {
    this.control({ key, label, section, type: 'select', options: values.map(value => ({ value, label: value })), ...(help ? { help } : {}) });
    return this.add({ pdfField, source: `probateFiling.${key}`, label });
  }
  /**
   * One decision printed as marks. `targets[i]` lists the PDF fields for option i.
   * Real checkboxes are checked; text boxes drawn as squares receive "X".
   */
  choice(key: string, label: string, section: string, options: { label: string; fields: string[]; value?: string }[], kind: 'checkbox' | 'mark' = 'mark', help?: string) {
    this.control({ key, label, section, type: 'select', options: options.map(o => ({ value: o.value ?? o.label, label: o.label })), ...(help ? { help } : {}) });
    options.forEach((o, i) => o.fields.forEach(pdfField => this.add({
      pdfField, source: `probateFiling.${key}${kind === 'mark' ? 'Mark' : 'Choice'}${i}`, label: `${label}: ${o.label}`,
    })));
    return this;
  }
  /** Declare a participant selection. Their details stay in Participants. */
  person(key: string, label: string, section: string, help = 'Select an existing participant. Edit their details in Participants.') {
    return this.control({ key: `${key}Uid`, label, section, type: 'participant', help });
  }
  /** Print one detail of a selected participant. */
  personField(pdfField: string, key: string, field: PersonField, label: string) {
    if (!this.schema.controls.some(c => c.key === `${key}Uid`)) throw new Error(`${this.schema.title}: unknown person ${key}`);
    return this.add({ pdfField, source: `probateFiling.${key}_${field}`, label });
  }
  /** Declare a list of participant selections (one per printed row). */
  people(key: string, label: string, section: string, max: number, note?: string, help = 'Select existing participants in the order they should be printed. Edit their details in Participants.') {
    return this.control({ key, label, section, type: 'participants', max, ...(note ? { note } : {}), help });
  }
  /** Print one detail of row `row` (0-based) of a people list. Use field 'note' for the per-row text. */
  peopleField(pdfField: string, key: string, row: number, field: PersonField | 'note', label: string) {
    const c = this.schema.controls.find(x => x.key === key);
    if (!c || c.type !== 'participants') throw new Error(`${this.schema.title}: unknown people list ${key}`);
    if (row >= (c.max ?? 0)) throw new Error(`${this.schema.title}: ${key} row ${row} exceeds max ${c.max}`);
    return this.add({ pdfField, source: `probateFiling.${key}_${row}_${field}`, label });
  }
  /** Print a composition of filing answers and shared values. */
  filingTemplate(pdfField: string, template: string, label: string) {
    return this.add({ pdfField, source: 'probateFiling.template', label, template });
  }
  /** A value fixed by the nature of the filing (for example SS-4 entity type "Estate"). */
  fixed(pdfField: string, value: 'checked' | 'unchecked' | 'X', label: string, reason: string) {
    const key = value === 'checked' ? 'fixedTrue' : value === 'unchecked' ? 'fixedFalse' : 'fixedX';
    return this.add({ pdfField, source: `probateFiling.${key}`, label, location: reason });
  }
  signature(pdfField: string, label: string) { return this.add({ pdfField, source: null, label, execution: 'signature', location: SIGNATURE_NOTE }); }
  notary(pdfField: string, label: string) { return this.add({ pdfField, source: null, label, execution: 'notary', location: NOTARY_NOTE }); }
  court(pdfField: string, label: string) { return this.add({ pdfField, source: null, label, execution: 'court', location: COURT_NOTE }); }
  notApplicable(pdfField: string, label: string, reason: string) { return this.add({ pdfField, source: null, label, execution: 'notApplicable', location: reason }); }
}

export const filing = (file: string, title: string, build: (f: FilingBuilder) => void): ProbateFilingSchema => {
  const b = new FilingBuilder(file, title);
  build(b);
  return b.schema;
};
